import TablePagination, { TablePaginationRef } from "../../../components/TablePagination";
import api from "../../../services/api";
import { currencyMask, displayDateTime } from "../../../utils/masks";
import IPersonBilling from "../../../models/IPersonBilling";
import { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNoteSticky, faPencil, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { authenticationService } from "../../../services/auth.service";
import { Link } from "react-router-dom";
import CampaignPayment from "../campaign-payment/CampaignPayment";
import IPersonCampaignDTO from "../../../models/IPersonCampaignDTO";
import moment from "../../../utils/momentConfig";
import { isMobile } from "react-device-detect";
import { toast } from "react-toastify";
import toastOptions from "../../../utils/toastOptions";

interface Params {
  id?: number;
}

export default function CampaignPaymentList(props: Params) {
  const { id } = props;

  const [hasPerson, setHasPerson] = useState<boolean>(false);
  const [termSearch, setTermSearch] = useState<string>("");
  const [delayedSearchTerm, setDelayedSearchTerm] = useState("");
  const [refresh, setRefresh] = useState<boolean>();
  const [editPayment, setEditPayment] = useState<boolean>(false);
  const [person, setPerson] = useState<IPersonCampaignDTO>();
  const [updatedRow, setUpdatedRow] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [year, setYear] = useState<number>();
  const [month, setMonth] = useState<number>();
  const [isNotDelivered, setNotDelivered] = useState<boolean>();
  const [resendStatus, setResendStatus] = useState<Map<number, boolean>>(new Map());
  const tableRef = useRef<TablePaginationRef>(null);

  const columns = [
    {
      name: "Nome",
      grow: 2,
      sortable: true,
      omit: hasPerson,
      cell: (row: IPersonBilling) => 
        <Link to={`/campanhas/pessoas/${row.person.idPersonCampaign}`} 
          uk-tooltip="title: Ver doador"
          className="cursor-pointer"
        >
          <span>{row.person.name} {`(${row.person.congregation?.description})`}</span>
        </Link>
    },
    {
      name: "Parcela",
      sortable: false,
      cell: (row: IPersonBilling) => <div className="uk-flex uk-flex-column">
        <span className="uk-text-bold">Parcela {row.numOfPayment ?? 0}</span>
        <span className="uk-text-muted">({row.month}/{row.year})</span>
      </div>
    },
    {
      name: "Pagamento",
      sortable: false,
      cell: (row: IPersonBilling, index: number) => <div className="uk-flex uk-flex-middle">
        <div className="uk-flex uk-flex-column">        
          <span>
            {currencyMask(row.paidAmount)}
          </span>
          <div className="uk-flex uk-flex-column">
            <span className="uk-text-italic">{displayDateTime(row.paidAt)}</span>
            <span className="uk-text-muted">{row.paymentType}</span>
          </div>
        </div>
        {!row.deliveredAt &&
          <a href="#"
            onClick={(ev) => {ev.preventDefault(); handleResentReceipt(row, index)}}
          >
            {              
              resendStatus.get(row.id)? <div className="uk-margin-small-left" uk-spinner="true; ratio:0.5"></div> :
                <FontAwesomeIcon icon={faTriangleExclamation} 
                  className="uk-text-warning uk-margin-small-left"
                  uk-tooltip="title: Recibo não enviado via WhatsApp"
                  size={isMobile ? "lg" : "1x"} />
            }
          </a>
        }
        {(row.note && row.note?.length > 0) &&
          <FontAwesomeIcon icon={faNoteSticky}
            className="uk-text-primary uk-margin-small-left cursor-pointer"
            uk-tooltip={`title: ${row.note}`}
            size={isMobile ? "lg" : "1x"} />
        }        
      </div>
    },
    {
      name: "Ações",
      button: true,
      sortable: false,
      cell: (row: IPersonBilling) => (
        <a href="#" uk-tooltip="title: Editar"
          onClick={(ev) => { ev.preventDefault(); handleEdit(row) }}
        >
          <FontAwesomeIcon icon={faPencil} />
        </a>
      ),
      omit: !authenticationService.canChange()
    }
  ];

  useEffect(() => {
    setHasPerson(id && id > 0 ? true : false);
  }, [refresh]);

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm]);

  const fetchPayments = async (page: number, limit: number) => {
    try {
      let requestParams = {
        page: page,
        limit: limit,
        idPerson: id,
        year: year,
        month: month,
        notDeliveredAt: isNotDelivered
      } as any;

      if (termSearch) {
        requestParams = { ...requestParams, name: termSearch?.toUpperCase() };
      }

      setPageNo(page);

      const response = await api.get(`/campaigns/payments`, { params: requestParams });

      return response;
    } catch {
      return { data: [], total: 0 };
    }
  };

  const handleSearchByName = async (search: string) => {
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
    }
  };

  const handleYear = async(selectedYear: number) => {
    setYear(selectedYear);
    setRefresh(!refresh);
  }

  const handleMonth = async(selectedMonth: number) => {
    setMonth(selectedMonth);
    setRefresh(!refresh);
  }

  const handleEdit = (row: IPersonBilling) => {
    setPerson({
      id: row.id,
      idPerson: row.idPerson,
      idBilling: row.id,
      name: row.person.name
    } as IPersonCampaignDTO);
    setEditPayment(true);
  }
  
  const afterHandleSubmit = (paidDate: Date) => {
    if (person) {
      person.paidDate = paidDate;
    }
    afterCloseModal();
    setUpdatedRow(!updatedRow);
  }

  const afterCloseModal = () => {
    setEditPayment(false);
    setPerson({} as IPersonCampaignDTO);    
  }

  const handleNotDelivered = async(selected: boolean) => {
    setNotDelivered(selected);
    setRefresh(!refresh);
  }

  const handleResentReceipt = async(row: IPersonBilling, index: number) => {
    const newLoadingStatus = new Map(resendStatus);
    newLoadingStatus.set(row.id, true);
    setResendStatus(newLoadingStatus);

    try {
      await api.put(`campaigns/payments/receipt/${row.id}`);
      toast.success(`Recibo reenviado com sucesso para ${row.person.name}!`, toastOptions);
      row.deliveredAt = new Date();
      handleUpdateRow(index, row);
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
    } finally {
      setResendStatus(prev => {
        const newMap = new Map(prev);
        newMap.delete(row.id);
        return newMap;
      });
    }
  }

  // To update a row:
  const handleUpdateRow = (index: number, data: any) => {
    if (tableRef.current) {
      tableRef.current.updateRowByIndex(index, data);
    }
  };

  return (
    <div id="page-campaign-person-payment-list">
      <div className="uk-card uk-card-default uk-card-small uk-card-hover uk-margin-bottom">
        {!hasPerson &&
          <div className="uk-card-header">
            <div className="uk-grid-small uk-flex-middle" uk-grid="true">
              <div className="uk-width-expand">
                <h3 className="uk-card-title uk-margin-remove-bottom">
                  Pagamentos Realizados
                </h3>
              </div>
              <div className="uk-flex@m uk-flex-middle">
                <div className="uk-inline-clip uk-transition-toggle uk-margin-small-right">
                  <div className="uk-transition-scale-up uk-transition-opaque">
                    <Link to="/campanhas" className="uk-button uk-button-small uk-button-primary">
                      Voltar
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
        <div className="uk-card-body">
          {!hasPerson &&
            <div className="uk-grid-small uk-margin-top" uk-grid="true">
              <div className="uk-search uk-search-default uk-width-1-3@s">
                <span
                  className="uk-search-icon-flip"
                  uk-search-icon="true"
                ></span>
                <input
                  className="uk-search-input uk-input uk-form-small"
                  type="search"
                  placeholder="Pesquise por nome..."
                  style={{ textTransform: "uppercase" }}
                  onChange={(ev) => {
                    handleSearchByName(ev.target.value);
                  }}
                />
              </div>
              <div className="uk-width-1-6@s">
                <div className="uk-flex uk-flex-middle">                
                  <label className="uk-form-label uk-margin-small-right" htmlFor="year">Ano</label>
                  <input type="number" 
                    id="year" 
                    name="year" 
                    placeholder="Ano" 
                    className="uk-input uk-form-small" 
                    defaultValue={year}
                    onChange={(e) => { handleYear(+e.target.value) }}
                  />
                </div>
              </div>
              <div className="uk-width-1-6@s">
                <div className="uk-flex uk-flex-middle">                
                  <label className="uk-form-label uk-margin-small-right" htmlFor="month">Mês</label>
                  <div uk-form-custom="target: > * > span:first-child" 
                    className="uk-width-1"
                    style={{backgroundColor: '#FFF'}}>
                    <select className="uk-form-small"
                      id="month"
                      name="month"
                      value={month}
                      onChange={(e) => {handleMonth(+e.target.value);}}
                    >                                    
                      <option value="">Todos</option>
                      {
                        moment.months().map((m, i) => {
                          return <option key={`month${i+1}`} value={i+1}>{m.charAt(0).toUpperCase() + m.slice(1).toLowerCase()}</option>
                        })
                      }
                    </select>
                    <button 
                      className="uk-button uk-button-default uk-button-select uk-form-small uk-width-1 uk-flex uk-flex-middle uk-flex-between"
                      style={{textTransform: 'none'}} 
                      type="button" 
                      tabIndex={-1}
                    >
                      <span></span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
              </div>
              <div className="uk-width-1-3@s">
                <label className="uk-form-label uk-margin-small-right" htmlFor="type">WhatsApp não entregue?</label>
                <input className="uk-checkbox"
                  name="notDelivered"
                  onChange={(e) => {
                    handleNotDelivered(e.target.checked);
                  }}
                  type="checkbox"/>
              </div> 
            </div>
          }
          <TablePagination
            ref={tableRef}
            columns={columns}
            fetchData={fetchPayments}
            refresh={refresh}
            updateRow={updatedRow}
            pageNo={pageNo}
          />
        </div>
      </div>
      <CampaignPayment 
        open={editPayment}
        afterHandleSubmit={afterHandleSubmit}
        afterCloseModal={afterCloseModal}
        personCampaign={person}
      />
    </div>
  );
}
